import {
  AMBER, CONTENT_TOP, FONT, INK, MUTED, NAVY, SLIDE_H, SLIDE_W,
  downloadCanvasPng, ellipsize, fitText, loadSlideBranding, roundRect, slideFileName, startSlide,
} from './slideCanvas';

// ── Rooming List slide ──────────────────────────────────────────────────────
// 1920×1080 PNG(s) of the tour's rooms and who is in each — the same look as
// the Team Categories slide, for the Canva welcome presentation. Big tours are
// split evenly across several slides so names stay readable.

export const MAX_ROOMS_PER_SLIDE = 24;

const SIDE = 72;
const GAP = 28;
const AREA_W = SLIDE_W - SIDE * 2;
const AREA_H = SLIDE_H - CONTENT_TOP - 56;

function fullName(p) {
  return [p.firstName, p.surname].filter(Boolean).join(' ') || p.email || '';
}

/**
 * The slide content: [{ label, beds, staff, occupants: [name] }] — guest rooms
 * in layout order, then MST staff rooms. Participants who have since been
 * removed from the tour are dropped.
 */
export function roomingListData(layout, participants) {
  const byId = Object.fromEntries((participants || []).map((p) => [p.id, p]));
  const assignments = layout?.assignments || {};
  const staffAssignments = layout?.staffAssignments || {};
  const rooms = (layout?.rooms || []).map((room) => ({
    label: room.label || 'Room',
    beds: Number(room.beds) || 0,
    staff: false,
    occupants: (assignments[room.id] || []).map((pid) => byId[pid]).filter(Boolean).map(fullName).filter(Boolean),
  }));
  const staffRooms = (layout?.staffRooms || []).map((room) => ({
    label: room.label || 'Staff Room',
    beds: 0,
    staff: true,
    occupants: (staffAssignments[room.id] || []).map((s) => s.name).filter(Boolean),
  }));
  return [...rooms, ...staffRooms];
}

/** Splits the rooms evenly across as few slides as fit MAX_ROOMS_PER_SLIDE. */
export function paginateRooms(rooms, max = MAX_ROOMS_PER_SLIDE) {
  if (!rooms.length) return [];
  const pages = Math.ceil(rooms.length / max);
  const per = Math.ceil(rooms.length / pages);
  const out = [];
  for (let i = 0; i < rooms.length; i += per) out.push(rooms.slice(i, i + per));
  return out;
}

// Card proportions, all relative to the name font size `s`.
const PAD = 20;
const headH = (s) => Math.round(s * 2.4 + 10);
const lineH = (s) => s * 1.6;

/**
 * Picks the column count that gives the largest readable name size for these
 * rooms: tall enough for the fullest room, wide enough for the longest name.
 */
export function roomGrid(rooms) {
  const n = Math.max(rooms.length, 1);
  const maxOcc = Math.max(1, ...rooms.map((r) => r.occupants.length));
  const longest = Math.max(8, ...rooms.flatMap((r) => r.occupants.map((name) => name.length)));
  let best = null;
  for (let cols = 1; cols <= Math.min(n, 8); cols += 1) {
    const rows = Math.ceil(n / cols);
    const cardW = Math.min(440, (AREA_W - GAP * (cols - 1)) / cols);
    const cardH = (AREA_H - GAP * (rows - 1)) / rows;
    const byHeight = (cardH - PAD * 2 - 10) / (2.4 + 1.6 * maxOcc);
    const byWidth = (cardW - PAD * 2 - 18) / (longest * 0.52);
    const size = Math.min(30, byHeight, byWidth);
    // On a tie, more columns — fewer, wider rows read better on a slide.
    if (!best || size >= best.size) best = { cols, rows, cardW, cardH, size };
  }
  return { ...best, size: Math.max(12, Math.floor(best.size)) };
}

function drawRoom(ctx, room, x, y, w, h, s) {
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.28)';
  ctx.shadowBlur = 20;
  ctx.shadowOffsetY = 6;
  ctx.fillStyle = '#FFFFFF';
  roundRect(ctx, x, y, w, h, 16);
  ctx.fill();
  ctx.restore();

  // Header band — navy for guest rooms, amber for MST staff
  const hh = headH(s);
  ctx.save();
  roundRect(ctx, x, y, w, h, 16);
  ctx.clip();
  ctx.fillStyle = room.staff ? AMBER : NAVY;
  ctx.fillRect(x, y, w, hh);
  ctx.fillStyle = room.staff ? NAVY : AMBER;
  ctx.fillRect(x, y + hh, w, 5);
  ctx.restore();

  ctx.textBaseline = 'middle';
  ctx.textAlign = 'right';
  const tag = room.staff ? 'MST STAFF' : room.beds ? `${room.beds} BED${room.beds === 1 ? '' : 'S'}` : '';
  let tagW = 0;
  if (tag) {
    ctx.font = `800 ${Math.max(11, Math.round(s * 0.62))}px ${FONT}`;
    ctx.fillStyle = room.staff ? NAVY : 'rgba(255,255,255,0.65)';
    ctx.fillText(tag, x + w - PAD, y + hh / 2 + 1);
    tagW = ctx.measureText(tag).width + 12;
  }
  ctx.textAlign = 'left';
  ctx.fillStyle = room.staff ? NAVY : '#FFFFFF';
  const label = room.label.toUpperCase();
  fitText(ctx, label, w - PAD * 2 - tagW, 900, Math.round(s * 1.25), 12);
  ctx.fillText(ellipsize(ctx, label, w - PAD * 2 - tagW), x + PAD, y + hh / 2 + 1);

  // Occupants
  let cy = y + hh + 5 + PAD * 0.7;
  ctx.textBaseline = 'middle';
  if (!room.occupants.length) {
    ctx.font = `italic 500 ${s}px ${FONT}`;
    ctx.fillStyle = MUTED;
    ctx.fillText(room.staff ? 'Staff to be confirmed' : 'To be confirmed', x + PAD, cy + lineH(s) / 2);
    return;
  }
  const dot = Math.max(3, s * 0.18);
  room.occupants.forEach((name) => {
    const my = cy + lineH(s) / 2;
    ctx.fillStyle = AMBER;
    ctx.beginPath(); ctx.arc(x + PAD + dot, my, dot, 0, Math.PI * 2); ctx.fill();
    ctx.font = `600 ${s}px ${FONT}`;
    ctx.fillStyle = INK;
    ctx.fillText(ellipsize(ctx, name, w - PAD * 2 - dot * 2 - 12), x + PAD + dot * 2 + 12, my);
    cy += lineH(s);
  });
}

/** Draws one slide of rooms onto `canvas` (resized to 1920×1080). */
export async function drawRoomingList(canvas, rooms, opts = {}) {
  const ctx = await startSlide(canvas, 'ROOMING LIST', opts);
  const { cols, rows, cardW, size } = roomGrid(rooms);
  // Cards only as tall as the fullest room needs, block centred vertically.
  const maxOcc = Math.max(1, ...rooms.map((r) => r.occupants.length));
  const fullH = (AREA_H - GAP * (rows - 1)) / rows;
  const cardH = Math.min(fullH, headH(size) + 5 + PAD * 1.7 + lineH(size) * maxOcc);
  const top = CONTENT_TOP + (AREA_H - (rows * cardH + (rows - 1) * GAP)) / 2;
  rooms.forEach((room, i) => {
    const row = Math.floor(i / cols);
    const inRow = Math.min(cols, rooms.length - row * cols);
    const rowW = inRow * cardW + (inRow - 1) * GAP;
    const x = (SLIDE_W - rowW) / 2 + (i % cols) * (cardW + GAP);
    const y = top + row * (cardH + GAP);
    drawRoom(ctx, room, x, y, cardW, cardH, size);
  });
}

/** Builds the slide(s) for a tour and downloads them as PNGs. */
export async function downloadRoomingListPng({ request, layout, participants }) {
  const pages = paginateRooms(roomingListData(layout, participants));
  const { tourName, logoSrc, crestSrc } = await loadSlideBranding(request);
  for (let i = 0; i < pages.length; i += 1) {
    const multi = pages.length > 1;
    const subtitle = multi ? `${tourName}${tourName ? ' · ' : ''}${i + 1} of ${pages.length}` : tourName;
    const canvas = document.createElement('canvas');
    await drawRoomingList(canvas, pages[i], { subtitle, logoSrc, crestSrc });
    await downloadCanvasPng(canvas, slideFileName(tourName, multi ? `Rooming_List_${i + 1}` : 'Rooming_List'));
    // Browsers drop back-to-back downloads fired in the same tick.
    if (multi) await new Promise((resolve) => setTimeout(resolve, 400));
  }
}
